import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, formatFileSize } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SignaturePlacementCanvas from "@/components/SignaturePlacementCanvas";
import { DropZone } from "@/components/DropZone";
import { FileText, Plus, Trash2, Pencil, MousePointerClick, Save, UploadCloud, Eye, AlignLeft, AlignCenter, AlignRight, AlignJustify, Variable, Sparkles, Download, Search, Loader2 } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

// 🆕 Feb 2026 — Configurazione Quill per modelli PDF Rich Text
const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["header", "size", "bold", "italic", "underline", "strike", "color", "background", "list", "align"];

// 🆕 Feb 2026 — CSS iniettato per evidenziare i token {{...}} dentro il Quill editor.
// Il chip mantiene lo sfondo giallo + testo scuro leggibile (slate-900).
// Il selettore matcha gli span con `background-color: rgb(254, 240, 138)` (il chip
// creato da insertToken). NON usiamo `!important` così quando l'utente applica un
// colore dalla toolbar, l'inline style `color: X` sovrascrive comunque questa regola.
const PDF_TPL_HIGHLIGHT_CSS = `
  .pdf-tpl-quill .ql-editor { min-height: 260px; font-size: 13px; }
  .pdf-tpl-quill .ql-editor span[style*="background-color: rgb(254, 240, 138)"] {
    color: #0f172a;
    padding: 0 3px;
    border-radius: 3px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.95em;
  }
`;

/**
 * 🆕 Feb 2026 — Modelli PDF – Policy/Quote
 * Sistema per creare template PDF riutilizzabili con segnaposto associati ai
 * dati reali di cliente + polizza/preventivo. Due modalità:
 *  - Visuale: overlay di token su PDF caricato (riusa il canvas della firma)
 *  - Testo:   editor con `{token}` → PDF semplice testo-puro
 */
const TOKEN_BOX_LABEL = {
  signature: "FIRMA",
  today: "DATA",
  today_place: "LUOGO E DATA",
  document_place: "LUOGO",
  client_name: "NOME",
  client_surname: "COGNOME",
  client_fiscal_code: "CF",
  client_piva: "P.IVA",
  client_full_name: "INTESTATARIO",
  client_address: "INDIRIZZO",
  client_address_full: "INDIRIZZO COMPLETO",
  client_address_with_civ: "INDIRIZZO + N. CIV.",
  client_house_number: "CIVICO",
  client_zip: "CAP",
  client_city: "CITTÀ",
  client_province: "PROV.",
  client_birth_date: "NATO IL",
  client_birth_place: "LUOGO NASCITA",
  client_marital_status: "STATO CIVILE",
  client_profession: "PROFESSIONE",
  client_company: "AZIENDA",
  client_email: "EMAIL",
  client_mobile: "CELLULARE",
  client_legal_rep_full: "L.R.",
  client_legal_rep_fiscal_code: "CF L.R.",
  client_legal_rep_role: "RUOLO L.R.",
  client_license_category: "CAT. PATENTE",
  client_license_first_issue: "1° RILASCIO PATENTE",
  client_license_issued_at: "RILASCIO PATENTE",
  client_license_expiry: "SCAD. PATENTE",
  policy_number: "N. POLIZZA",
  policy_vehicle_plate: "TARGA",
  policy_vehicle_brand: "MARCA",
  policy_vehicle_model: "MODELLO",
  policy_vehicle_version: "VERSIONE",
  policy_vehicle_fuel: "ALIMENTAZIONE",
  policy_vehicle_displacement: "CILINDRATA",
  policy_vehicle_kw: "kW",
  policy_vehicle_fiscal_power: "CV FISCALI",
  policy_vehicle_year: "ANNO IMM.",
  policy_vehicle_first_registration: "1ª IMM.",
  policy_vehicle_chassis: "TELAIO",
  policy_vehicle_use: "USO VEICOLO",
  policy_driving_type: "TIPO GUIDA",
  policy_youngest_driver_age: "ETÀ COND. + GIOVANE",
  policy_start_date: "DECORRENZA",
  policy_end_date: "SCADENZA",
  policy_payment_frequency: "FRAZIONAMENTO",
  policy_premium: "PREMIO LORDO",
  policy_net_premium: "PREMIO NETTO",
  policy_taxes: "IMPOSTE",
  policy_ssn: "SSN",
  policy_fee_broker: "FEE BROKER",
  policy_agency_fees: "DIRITTI AG.",
  policy_branch: "RAMO",
  policy_type: "TIPO",
  policy_company_name: "COMPAGNIA",
  policy_company_email: "EMAIL COMP.",
  policy_company_pec: "PEC COMP.",
  policy_company_phone: "TEL COMP.",
  policy_company_address: "IND. COMP.",
  policy_company_rui: "RUI COMP.",
  policy_company_contact_person: "REF. COMP.",
  coverages_table: "TAB. COPERTURE",
  agency_name: "AGENZIA",
  agency_email: "EMAIL AG.",
  agency_phone: "TEL AG.",
  agency_address: "IND. AG.",
  agency_city: "CITTÀ AG.",
};

function labelForPlacement(p) {
  if (!p) return "?";
  // 🆕 Feb 2026 — Placement statici: testo libero / casella di spunta / area cancellata
  if (p.type === "text") {
    const v = (p.text_value || "").trim();
    return v ? v.slice(0, 40) : "TESTO LIBERO";
  }
  if (p.type === "checkbox") return p.checked ? "☑" : "☐";
  if (p.type === "redact") return "🧽 CANCELLA";
  const t = p.token || "";
  return TOKEN_BOX_LABEL[t] || (t ? t.replace(/_/g, " ").toUpperCase() : "?");
}

export default function PdfTemplates() {
  const [items, setItems] = useState([]);
  const [tokensCatalog, setTokensCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // template in edit
  const [openDialog, setOpenDialog] = useState(false);
  // 🆕 Feb 2026 — Anteprima PDF dalla card (senza aprire l'editor)
  const [previewTpl, setPreviewTpl] = useState(null);

  const flatTokens = useMemo(() => tokensCatalog.flatMap((g) => g.tokens || []), [tokensCatalog]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: cat }] = await Promise.all([
        api.get("/admin/pdf-templates"),
        api.get("/admin/pdf-templates/tokens"),
      ]);
      setItems(list);
      setTokensCatalog(cat.catalog || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ name: "", description: "", mode: "html", placements: [], text_template: "", html_template: "", variables_used: [], file: null }); setOpenDialog(true); };
  const openEdit = (tpl) => {
    // 🆕 Fix Feb 17/2026: migrazione automatica dei modelli esistenti che avevano
    // i chip token forzati in blu (`color: rgb(30, 64, 175)`). Rimuoviamo l'inline
    // color=blu dagli span dei token in modo che ereditino il colore del paragrafo
    // e siano modificabili dall'utente via toolbar Quill.
    let cleaned = tpl.html_template || "";
    if (cleaned) {
      // Rimuove `color: rgb(30, 64, 175);` (o `color:rgb(30,64,175)` senza spazi)
      cleaned = cleaned.replace(/color\s*:\s*rgb\(\s*30\s*,\s*64\s*,\s*175\s*\)\s*;?\s*/gi, "");
      // Rimuove anche l'esadecimale equivalente #1e40af
      cleaned = cleaned.replace(/color\s*:\s*#1e40af\s*;?\s*/gi, "");
      // Rimuove attributo style="" vuoto residuo
      cleaned = cleaned.replace(/\s*style\s*=\s*["']\s*["']/gi, "");
    }
    setEditing({ ...tpl, html_template: cleaned, file: null });
    setOpenDialog(true);
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminare questo modello?")) return;
    try { await api.delete(`/admin/pdf-templates/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="animate-fade-up p-4 sm:p-6 max-w-6xl mx-auto" data-testid="pdf-templates-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-600" /> Modelli PDF – Policy/Quote</h1>
          <p className="text-sm text-slate-500 mt-1">Crea template riutilizzabili con segnaposto dati cliente + polizza. Applicabili dalla scheda polizza o preventivo.</p>
        </div>
        <Button onClick={openNew} data-testid="pdf-tpl-new" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="w-4 h-4 mr-1" /> Nuovo modello
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="py-16 rounded-lg border border-dashed border-slate-300 bg-white text-center text-slate-500">
          Nessun modello ancora. Crea il tuo primo modello PDF.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{t.name}</div>
                  <div className="text-[11px] uppercase font-bold text-emerald-700 mt-0.5">
                    {t.mode === "visual" ? "📐 Visuale" : t.mode === "html" ? "📄 Rich Text" : "📝 Testo"}
                  </div>
                  {t.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>}
                  {t.mode === "visual" && (
                    <div className="text-[11px] text-slate-500 mt-1">{(t.placements || []).length} segnaposto</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  {t.mode === "visual" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewTpl(t)}
                      data-testid={`pdf-tpl-preview-${t.id}`}
                      title="Anteprima PDF"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`pdf-tpl-edit-${t.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(t.id)} className="text-rose-600 hover:bg-rose-50" data-testid={`pdf-tpl-delete-${t.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TemplateEditorDialog
          open={openDialog}
          onClose={() => { setOpenDialog(false); setEditing(null); }}
          value={editing}
          tokensCatalog={tokensCatalog}
          flatTokens={flatTokens}
          onSaved={() => { load(); setOpenDialog(false); setEditing(null); }}
        />
      )}

      {/* 🆕 Feb 2026 — Anteprima PDF dalla card (senza aprire l'editor) */}
      <TemplatePreviewDialog
        open={!!previewTpl}
        onClose={() => setPreviewTpl(null)}
        tpl={previewTpl}
      />
    </div>
  );
}

function TemplatePreviewDialog({ open, onClose, tpl }) {
  const [pdfBlob, setPdfBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !tpl?.id) { setPdfBlob(null); setErr(null); return; }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api.get(`/admin/pdf-templates/${tpl.id}/preview-pdf`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const blob = new Blob([r.data], { type: "application/pdf" });
        setPdfBlob(blob);
      })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e) || "Impossibile caricare il PDF"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, tpl?.id]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-emerald-600" />
            Anteprima — {tpl?.name || "Modello PDF"}
          </DialogTitle>
          <DialogDescription>
            Visualizza il PDF base con i segnaposti configurati. Scorri per navigare tra le pagine.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-[300px]">
          {loading && <div className="py-16 text-sm text-slate-500 text-center">Caricamento PDF…</div>}
          {err && <div className="py-8 text-sm text-rose-600 text-center">{err}</div>}
          {!loading && !err && pdfBlob && (
            <SignaturePlacementCanvas
              file={pdfBlob}
              value={tpl?.placements || []}
              onChange={() => { /* readOnly */ }}
              labelFor={labelForPlacement}
              readOnly
              testid="pdf-tpl-preview-canvas"
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pdf-tpl-preview-close">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplateEditorDialog({ open, onClose, value, tokensCatalog, flatTokens, onSaved }) {
  const [t, setT] = useState(value);
  useEffect(() => { setT(value); }, [value]);
  const [saving, setSaving] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [selectedToken, setSelectedToken] = useState("");
  const [pdfBlobForCanvas, setPdfBlobForCanvas] = useState(null);
  const [previewDataOpen, setPreviewDataOpen] = useState(false); // 🆕 Anteprima con dati reali

  const isEdit = !!t.id;

  // Carica PDF base se editing visual
  useEffect(() => {
    let cancelled = false;
    async function fetchBase() {
      if (t.id && t.mode === "visual") {
        try {
          const res = await api.get(`/admin/pdf-templates/${t.id}/preview-pdf`, { responseType: "blob" });
          if (!cancelled) setPdfBlobForCanvas(res.data);
        } catch { /* silenzioso */ }
      }
    }
    fetchBase();
    return () => { cancelled = true; };
  }, [t.id, t.mode]);

  const currentFile = pdfFile || pdfBlobForCanvas;

  const save = async () => {
    if (!t.name?.trim()) { toast.error("Inserisci un nome"); return; }
    setSaving(true);
    try {
      // 🆕 Feb 2026 — Estrai i token {{...}} usati nel corpo Rich Text o Testo
      const bodyText = t.mode === "html" ? (t.html_template || "") : (t.text_template || "");
      const usedTokens = Array.from(new Set(
        Array.from(bodyText.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}|\{([a-zA-Z0-9_]+)\}/g))
          .map((m) => m[1] || m[2])
          .filter(Boolean)
      ));
      const fd = new FormData();
      fd.append("name", t.name);
      fd.append("description", t.description || "");
      fd.append("mode", t.mode);
      fd.append("placements", JSON.stringify(t.placements || []));
      fd.append("text_template", t.text_template || "");
      fd.append("html_template", t.html_template || "");
      fd.append("variables_used", JSON.stringify(usedTokens));
      if (pdfFile) fd.append("file", pdfFile);
      if (isEdit) {
        await api.put(`/admin/pdf-templates/${t.id}`, fd);
      } else {
        await api.post("/admin/pdf-templates", fd);
      }
      toast.success("Salvato");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const insertTokenInText = (tk) => {
    const wrap = `{${tk}}`;
    setT({ ...t, text_template: (t.text_template || "") + wrap });
  };

  // Nel canvas visuale: quando l'utente clicca sul PDF, aggiungiamo un placement col token / tipo selezionato
  const setPlacements = (next) => {
    // il canvas restituisce array con page/x/y/w/h — associamo il "tool" corrente all'ultimo aggiunto
    const arr = (next || []).map((p, i, all) => {
      const isLast = i === all.length - 1;
      const isNew = !p.token && !p.type;
      if (isLast && isNew && selectedToken) {
        // 🆕 Feb 2026 — pseudo-strumenti per elementi statici
        if (selectedToken === "__text__") return { ...p, type: "text", text_value: "" };
        if (selectedToken === "__checkbox__") return { ...p, type: "checkbox", checked: false };
        if (selectedToken === "__redact__") return { ...p, type: "redact", color: "#FFFFFF" };
        return { ...p, token: selectedToken };
      }
      return p;
    });
    setT({ ...t, placements: arr });
  };

  const updatePlacementToken = (idx, tk) => {
    const arr = [...(t.placements || [])];
    arr[idx] = { ...arr[idx], token: tk };
    setT({ ...t, placements: arr });
  };

  // 🆕 Feb 2026 — aggiornamento inline dei placement statici
  const updatePlacementText = (idx, val) => {
    const arr = [...(t.placements || [])];
    arr[idx] = { ...arr[idx], text_value: val };
    setT({ ...t, placements: arr });
  };
  const togglePlacementChecked = (idx) => {
    const arr = [...(t.placements || [])];
    arr[idx] = { ...arr[idx], checked: !arr[idx].checked };
    setT({ ...t, placements: arr });
  };

  // 🆕 Feb 2026 — Allineamento testo (left / center / right / justify)
  const updatePlacementAlign = (idx, align) => {
    const arr = [...(t.placements || [])];
    arr[idx] = { ...arr[idx], align };
    setT({ ...t, placements: arr });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="pdf-tpl-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica modello PDF" : "Nuovo modello PDF"}</DialogTitle>
          <DialogDescription>Compila i dati, scegli modalità e posiziona i segnaposto.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-3">
            <Label>Nome</Label>
            <Input value={t.name || ""} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="pdf-tpl-name" />
          </div>
          <div>
            <Label>Modalità</Label>
            <select className="w-full border rounded-md px-2 py-2 text-sm" value={t.mode} onChange={(e) => setT({ ...t, mode: e.target.value })} data-testid="pdf-tpl-mode">
              <option value="html">📄 Rich Text</option>
              <option value="visual">📐 Visuale (overlay PDF)</option>
              {t.mode === "text" && <option value="text">📝 Testo (legacy)</option>}
            </select>
          </div>
          <div className="md:col-span-4">
            <Label>Descrizione (opzionale)</Label>
            <Input value={t.description || ""} onChange={(e) => setT({ ...t, description: e.target.value })} />
          </div>
        </div>

        {/* Token picker (visual e text legacy) — nascosto per Rich Text (usa Popover inline) */}
        {t.mode !== "html" && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2.5">
          <div className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
            <MousePointerClick className="w-4 h-4 text-emerald-600" />
            Token disponibili — {t.mode === "visual" ? "seleziona, poi clicca sul PDF per posizionare" : "clicca per inserire in cursore"}
          </div>
          {/* 🆕 Feb 2026 — Elementi statici (testo libero + crocetta) */}
          {t.mode === "visual" && (
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Elementi statici</div>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedToken("__text__")}
                  className={`text-[10.5px] px-2 py-1 rounded border transition-colors inline-flex items-center gap-1 ${
                    selectedToken === "__text__"
                      ? "bg-indigo-600 text-white border-indigo-700"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-indigo-50 hover:border-indigo-400"
                  }`}
                  data-testid="token-btn-__text__"
                  title="Testo libero (statico)"
                >
                  <span className="text-sm leading-none">✏️</span> Testo libero
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedToken("__checkbox__")}
                  className={`text-[10.5px] px-2 py-1 rounded border transition-colors inline-flex items-center gap-1 ${
                    selectedToken === "__checkbox__"
                      ? "bg-indigo-600 text-white border-indigo-700"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-indigo-50 hover:border-indigo-400"
                  }`}
                  data-testid="token-btn-__checkbox__"
                  title="Casella di spunta con crocetta"
                >
                  <span className="text-sm leading-none">☑</span> Flag (crocetta)
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedToken("__redact__")}
                  className={`text-[10.5px] px-2 py-1 rounded border transition-colors inline-flex items-center gap-1 ${
                    selectedToken === "__redact__"
                      ? "bg-rose-600 text-white border-rose-700"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-rose-50 hover:border-rose-400"
                  }`}
                  data-testid="token-btn-__redact__"
                  title="Cancella un'area del PDF (redazione)"
                >
                  <span className="text-sm leading-none">🧽</span> Cancella area
                </button>
              </div>
            </div>
          )}
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {tokensCatalog.map((g) => (
              <div key={g.group}>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{g.group}</div>
                <div className="flex flex-wrap gap-1">
                  {(g.tokens || []).map((tk) => (
                    <button
                      key={tk.token}
                      type="button"
                      onClick={() => t.mode === "visual" ? setSelectedToken(tk.token) : insertTokenInText(tk.token)}
                      className={`text-[10.5px] px-2 py-1 rounded border transition-colors ${
                        selectedToken === tk.token && t.mode === "visual"
                          ? "bg-emerald-600 text-white border-emerald-700"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-emerald-50 hover:border-emerald-400"
                      }`}
                      data-testid={`token-btn-${tk.token}`}
                      title={tk.token}
                    >
                      {tk.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {t.mode === "visual" && selectedToken && (
            <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              {selectedToken === "__text__" ? (
                <>Strumento attivo: <strong>Testo libero</strong> — Clicca o trascina sul PDF per creare un box, poi digita il testo nel campo qui sotto.</>
              ) : selectedToken === "__checkbox__" ? (
                <>Strumento attivo: <strong>Casella (crocetta)</strong> — Clicca sul PDF per aggiungere una casella. Attiva/disattiva la crocetta qui sotto.</>
              ) : selectedToken === "__redact__" ? (
                <>Strumento attivo: <strong>Cancella area</strong> — Trascina sul PDF per coprire una parte di testo con un rettangolo bianco.</>
              ) : (
                <>Token attivo: <strong>{selectedToken}</strong> — Clicca sul PDF per aggiungerlo.</>
              )}
            </div>
          )}
        </div>
        )}

        {t.mode === "visual" ? (
          <div className="mt-3">
            <Label>PDF base</Label>
            <DropZone
              accept={["application/pdf"]}
              multiple={false}
              onFiles={(files) => { if (files && files[0]) setPdfFile(files[0]); }}
              testid="pdf-tpl-dropzone"
              className="mt-1 cursor-pointer border-2 border-dashed border-slate-300 rounded-lg hover:border-emerald-400 hover:bg-emerald-50/40 bg-slate-50 transition-colors"
            >
              <label className="block cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                  className="sr-only"
                  data-testid="pdf-tpl-file"
                />
                <div className="flex flex-col items-center justify-center gap-1.5 py-6 px-4 text-center">
                  <UploadCloud className="w-8 h-8 text-slate-400" />
                  {pdfFile ? (
                    <>
                      <div className="text-sm font-semibold text-slate-800">{pdfFile.name}</div>
                      <div className="text-[11px] text-slate-500">{formatFileSize(pdfFile.size)} — clicca o trascina per sostituire</div>
                    </>
                  ) : pdfBlobForCanvas ? (
                    <>
                      <div className="text-sm font-semibold text-emerald-700">PDF base già caricato</div>
                      <div className="text-[11px] text-slate-500">Trascina qui un nuovo PDF per sostituirlo, oppure clicca</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium text-slate-700">Trascina qui il PDF base</div>
                      <div className="text-[11px] text-slate-500">oppure clicca per selezionarlo dal computer</div>
                    </>
                  )}
                </div>
              </label>
            </DropZone>
            {(pdfFile || pdfBlobForCanvas) && (
              <div className="mt-3">
                <SignaturePlacementCanvas
                  file={currentFile}
                  value={t.placements}
                  onChange={setPlacements}
                  labelFor={labelForPlacement}
                  testid="pdf-tpl-canvas"
                />
                {(t.placements || []).length > 0 && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-white p-2">
                    <div className="text-[11px] font-semibold text-slate-600 mb-1">Segnaposto configurati:</div>
                    <ul className="space-y-1">
                      {(t.placements || []).map((p, i) => {
                        const supportsAlign = p.type === "text" || (!p.type || p.type === "token");
                        return (
                        <li key={i} className="flex flex-wrap gap-2 items-center text-[11px]">
                          <span className="shrink-0 text-slate-500 w-24">
                            #{i + 1} · pag.{p.page}
                            {p.type === "text" && <span className="ml-1 inline-flex items-center rounded bg-indigo-100 text-indigo-700 px-1 py-0 text-[9px] font-semibold">TESTO</span>}
                            {p.type === "checkbox" && <span className="ml-1 inline-flex items-center rounded bg-indigo-100 text-indigo-700 px-1 py-0 text-[9px] font-semibold">FLAG</span>}
                            {p.type === "redact" && <span className="ml-1 inline-flex items-center rounded bg-rose-100 text-rose-700 px-1 py-0 text-[9px] font-semibold">CANCELLA</span>}
                          </span>
                          {p.type === "text" ? (
                            <Input
                              value={p.text_value || ""}
                              onChange={(e) => updatePlacementText(i, e.target.value)}
                              placeholder="Testo da inserire nel PDF…"
                              className="flex-1 min-w-[160px] h-6 text-[11px]"
                              data-testid={`pl-text-${i}`}
                            />
                          ) : p.type === "checkbox" ? (
                            <button
                              type="button"
                              onClick={() => togglePlacementChecked(i)}
                              className={`flex-1 min-w-[160px] h-6 text-[11px] rounded border px-2 inline-flex items-center gap-2 justify-start ${
                                p.checked ? "bg-indigo-600 text-white border-indigo-700" : "bg-white text-slate-700 border-slate-300 hover:bg-indigo-50"
                              }`}
                              data-testid={`pl-check-${i}`}
                              title="Alterna crocetta"
                            >
                              <span className="text-base leading-none">{p.checked ? "☑" : "☐"}</span>
                              <span className="uppercase tracking-wide">{p.checked ? "Selezionato" : "Non selezionato"}</span>
                            </button>
                          ) : p.type === "redact" ? (
                            <div className="flex-1 min-w-[160px] h-6 flex items-center gap-2 text-[11px] text-slate-600">
                              <span className="uppercase tracking-wide">Copre l&apos;area sottostante</span>
                              <label className="ml-auto inline-flex items-center gap-1 cursor-pointer" title="Colore copertura">
                                <span className="text-[10px] text-slate-500">Colore:</span>
                                <input
                                  type="color"
                                  value={p.color || "#FFFFFF"}
                                  onChange={(e) => {
                                    const arr = [...(t.placements || [])];
                                    arr[i] = { ...arr[i], color: e.target.value };
                                    setT({ ...t, placements: arr });
                                  }}
                                  className="h-5 w-6 border border-slate-300 rounded cursor-pointer p-0"
                                  data-testid={`pl-color-${i}`}
                                />
                              </label>
                            </div>
                          ) : (
                            <select
                              value={p.token || ""}
                              onChange={(e) => updatePlacementToken(i, e.target.value)}
                              className="flex-1 min-w-[160px] border rounded px-1 py-0.5 text-[11px]"
                              data-testid={`pl-token-${i}`}
                            >
                              <option value="">— seleziona token —</option>
                              {flatTokens.map((tk) => <option key={tk.token} value={tk.token}>{tk.label}</option>)}
                            </select>
                          )}
                          {/* 🆕 Feb 2026 — Allineamento (solo testo libero e token dinamici) */}
                          {supportsAlign && (
                            <AlignPicker
                              value={p.align || "left"}
                              onChange={(v) => updatePlacementAlign(i, v)}
                              testid={`pl-align-${i}`}
                              hideJustify={!p.type || p.type === "token"}
                            />
                          )}
                          <span className="shrink-0 tabular-nums text-slate-400 w-14 text-right">{Math.round((p.x||0)*100)}%,{Math.round((p.y||0)*100)}%</span>
                          <button type="button" onClick={() => setT({ ...t, placements: t.placements.filter((_, ii) => ii !== i) })} className="shrink-0 text-rose-600 hover:bg-rose-50 rounded p-0.5" title="Rimuovi">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </li>
                      );})}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : t.mode === "html" ? (
          <RichTextTemplateEditor
            t={t}
            setT={setT}
            tokensCatalog={tokensCatalog}
          />
        ) : (
          <div className="mt-3">
            <Label>Testo del modello</Label>
            <Textarea
              rows={12}
              value={t.text_template || ""}
              onChange={(e) => setT({ ...t, text_template: e.target.value })}
              placeholder={"Es.\nSpett.le {client_full_name}\nCF: {client_fiscal_code}\n\nOggetto: Polizza n° {policy_number}\n...\n\n{coverages_table}"}
              className="font-mono text-xs"
              data-testid="pdf-tpl-text"
            />
            <p className="text-[10px] text-slate-500 mt-1">Sintassi <code>{'{token}'}</code>. Il token speciale <code>{'{coverages_table}'}</code> viene sostituito con la lista di coperture.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pdf-tpl-cancel">Annulla</Button>
          {isEdit && (t.mode === "html" || t.mode === "text" || t.mode === "visual") && (
            <Button
              variant="outline"
              onClick={() => setPreviewDataOpen(true)}
              data-testid="pdf-tpl-preview-data"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Anteprima con dati reali
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="pdf-tpl-save">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Salvataggio…" : "Salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <PreviewWithDataDialog
        open={previewDataOpen}
        onClose={() => setPreviewDataOpen(false)}
        tpl={t}
      />
    </Dialog>
  );
}


// 🆕 Feb 2026 — Selettore compatto di allineamento per placement token/testo
function AlignPicker({ value = "left", onChange, testid, hideJustify = false }) {
  const opts = [
    { v: "left", Icon: AlignLeft, title: "Allinea a sinistra" },
    { v: "center", Icon: AlignCenter, title: "Centra" },
    { v: "right", Icon: AlignRight, title: "Allinea a destra" },
    ...(hideJustify ? [] : [{ v: "justify", Icon: AlignJustify, title: "Giustifica (solo testo multi-riga)" }]),
  ];
  return (
    <div className="inline-flex items-center gap-0.5 border border-slate-300 rounded overflow-hidden bg-white shrink-0" data-testid={testid}>
      {opts.map(({ v, Icon, title }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange && onChange(v)}
          className={`h-6 w-6 flex items-center justify-center transition-colors ${
            value === v ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50"
          }`}
          title={title}
          data-testid={`${testid}-${v}`}
        >
          <Icon className="h-3 w-3" />
        </button>
      ))}
    </div>
  );
}


// 🆕 Feb 2026 — Editor Rich Text (Quill) con menu "Inserisci Variabile"
// Sostituisce la Textarea grezza. Toolbar completa: grassetto, corsivo, sottolineato,
// allineamento, dimensione testo, elenchi puntati/numerati, colori.
function RichTextTemplateEditor({ t, setT, tokensCatalog }) {
  const quillRef = useRef(null);
  const [varMenuOpen, setVarMenuOpen] = useState(false);
  const [varFilter, setVarFilter] = useState("");

  // Inserisce {{token}} nella posizione corrente del cursore Quill.
  // 🆕 Fix Feb 17/2026 (v3): PRESERVA il format corrente del cursore (color/bold/
  // italic/size scelti dall'utente dalla toolbar) inserendo il testo SENZA
  // attributes espliciti, poi applica il background giallo con `formatText`
  // solo al range appena inserito. Così il colore impostato dall'utente viene
  // rispettato.
  const insertToken = (tk) => {
    const editor = quillRef.current?.getEditor?.();
    const wrap = `{{${tk}}}`;
    if (editor) {
      const range = editor.getSelection(true) || { index: editor.getLength(), length: 0 };
      // Inserisci senza attributes → eredita il format corrente (color, bold, ecc.)
      editor.insertText(range.index, wrap);
      // Applica il background giallo SOLO al range del token, senza toccare altri attributi
      editor.formatText(range.index, wrap.length, "background", "#fef08a");
      // Sposta il cursore dopo il token
      editor.setSelection(range.index + wrap.length, 0);
      // Reset SOLO del background per non continuare a scrivere in giallo dopo il chip
      editor.format("background", false);
      // Aggiorna stato React (Quill notifica onChange, ma per sicurezza aggiorniamo html)
      setTimeout(() => {
        const html = editor.root.innerHTML;
        setT((prev) => ({ ...prev, html_template: html }));
      }, 10);
    } else {
      setT({ ...t, html_template: (t.html_template || "") + wrap });
    }
    setVarMenuOpen(false);
    setVarFilter("");
  };

  const filteredCatalog = useMemo(() => {
    const q = varFilter.trim().toLowerCase();
    if (!q) return tokensCatalog || [];
    return (tokensCatalog || []).map((g) => ({
      ...g,
      tokens: (g.tokens || []).filter((tk) =>
        (tk.label || "").toLowerCase().includes(q) ||
        (tk.token || "").toLowerCase().includes(q)
      ),
    })).filter((g) => g.tokens.length > 0);
  }, [tokensCatalog, varFilter]);

  return (
    <div className="mt-3" data-testid="pdf-tpl-html-editor">
      <style>{PDF_TPL_HIGHLIGHT_CSS}</style>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <Label className="mb-0">Corpo del modello — Rich Text</Label>
        <Popover open={varMenuOpen} onOpenChange={setVarMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="insert-variable-btn"
            >
              <Variable className="w-4 h-4 mr-1.5" /> Inserisci Variabile
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0 flex flex-col" align="end" style={{ maxHeight: "300px" }}>
            <div className="p-2 border-b border-slate-200 bg-slate-50 shrink-0">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  autoFocus
                  placeholder="Cerca variabile… (es. cliente, targa, premio)"
                  value={varFilter}
                  onChange={(e) => setVarFilter(e.target.value)}
                  className="pl-7 h-8 text-xs"
                  data-testid="insert-variable-search"
                />
              </div>
            </div>
            {/* 🆕 Fix Feb 17/2026: overflow-y scroll + max-height esplicita.
                Il Dialog Radix intercetta gli eventi wheel e non li propaga al
                Popover figlio: gestiamo manualmente `onWheel` per scrollare la
                lista con la rotellina del mouse. Anche `onTouchMove` per touch. */}
            <div
              ref={(el) => { if (el) el.style.overscrollBehavior = "contain"; }}
              className="flex-1 p-1.5"
              style={{ overflowY: "auto", minHeight: 0, maxHeight: "260px" }}
              data-testid="insert-variable-scroll"
              onWheel={(e) => {
                // Impedisce al Dialog padre di bloccare lo scroll interno
                e.stopPropagation();
                const el = e.currentTarget;
                const atTop = el.scrollTop === 0 && e.deltaY < 0;
                const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight && e.deltaY > 0;
                if (!atTop && !atBottom) {
                  el.scrollTop += e.deltaY;
                  e.preventDefault();
                }
              }}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {filteredCatalog.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-500">Nessuna variabile trovata</div>
              ) : (
                filteredCatalog.map((g) => (
                  <div key={g.group} className="mb-2 last:mb-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 px-2 py-1 bg-emerald-50 rounded-sm mb-1">
                      {g.group}
                    </div>
                    <div className="grid grid-cols-1 gap-0.5">
                      {(g.tokens || []).map((tk) => (
                        <button
                          key={tk.token}
                          type="button"
                          onClick={() => insertToken(tk.token)}
                          className="text-left text-xs px-2 py-1.5 rounded hover:bg-emerald-50 flex items-center justify-between gap-2 group"
                          data-testid={`var-insert-${tk.token}`}
                          title={`Inserisce {{${tk.token}}}`}
                        >
                          <span className="truncate text-slate-700 group-hover:text-emerald-800">{tk.label}</span>
                          <code className="text-[10px] text-slate-400 group-hover:text-emerald-600 font-mono shrink-0">{`{{${tk.token}}}`}</code>
                        </button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <div className="border border-slate-200 rounded-md bg-white pdf-tpl-quill">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={t.html_template || ""}
          onChange={(html) => setT({ ...t, html_template: html })}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Scrivi il corpo del modello. Usa la toolbar per formattare e il bottone 'Inserisci Variabile' per aggiungere i campi dinamici (es. {{client_full_name}})."
          data-testid="pdf-tpl-html-quill"
        />
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5 flex items-start gap-1">
        <Sparkles className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
        <span>
          Sintassi <code className="bg-slate-100 px-1 rounded">{'{{token}}'}</code>. Le variabili hanno sfondo <mark>giallo</mark>. Il colore del testo eredita dal paragrafo — <strong>seleziona</strong> il token e usa il color-picker della toolbar per cambiarlo.
          Il token speciale <code className="bg-slate-100 px-1 rounded">{'{{coverages_table}}'}</code> viene sostituito con la tabella coperture della polizza/preventivo.
        </span>
      </p>
    </div>
  );
}


// 🆕 Feb 2026 — Dialog "Anteprima con dati reali": due combobox di ricerca (polizze/preventivi)
// e iframe di anteprima PDF renderizzato con i dati reali del documento selezionato.
function PreviewWithDataDialog({ open, onClose, tpl }) {
  const [tab, setTab] = useState("policy"); // "policy" | "quote"
  const [policyQuery, setPolicyQuery] = useState("");
  const [quoteQuery, setQuoteQuery] = useState("");
  const [policyItems, setPolicyItems] = useState([]);
  const [quoteItems, setQuoteItems] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [genErr, setGenErr] = useState(null);

  // Reset quando dialog si chiude
  useEffect(() => {
    if (!open) {
      setSelectedId(null); setPdfUrl(null); setPdfBlob(null); setGenErr(null);
    }
  }, [open]);

  // Ricerca debounce
  useEffect(() => {
    if (!open) return;
    const q = tab === "policy" ? policyQuery : quoteQuery;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/pdf-templates/sample-sources`, {
          // 🆕 Fix Feb 17/2026: limit alto (500) per caricare TUTTE le polizze/preventivi
          params: { kind: tab, q, limit: 500 },
        });
        // 🆕 Fix Feb 18/2026: sort fallback client-side robusto.
        // Preferisce `_sort_key` (fornito dal server = cognome / ragione sociale
        // lowercase). Se assente, estrae l'ULTIMA parola dopo il "·" nel label
        // (che è sempre il cognome, es. "12456 · SANTO MAURIZIO ARENA" → "arena").
        const sortKey = (it) => {
          if (it._sort_key && String(it._sort_key).trim()) return String(it._sort_key).toLowerCase().trim();
          const clientPart = (it.label || "").split("·").slice(1).join("·").trim();
          if (!clientPart) return "zzzz";
          // Ultima parola = cognome (o unica parola se ragione sociale corta)
          const words = clientPart.split(/\s+/).filter(Boolean);
          return (words[words.length - 1] || "zzzz").toLowerCase();
        };
        const items = (data.items || []).slice().sort((a, b) => {
          return sortKey(a).localeCompare(sortKey(b), "it");
        });
        if (tab === "policy") setPolicyItems(items);
        else setQuoteItems(items);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, tab, policyQuery, quoteQuery]);

  const generatePreview = async (docId) => {
    setSelectedId(docId);
    setGenLoading(true); setGenErr(null);
    try {
      const body = tab === "policy" ? { policy_id: docId } : { quote_id: docId };
      const r = await api.post(`/admin/pdf-templates/${tpl.id}/preview-with-data`, body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      setPdfBlob(blob);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(blob));
    } catch (e) {
      setGenErr(formatApiError(e) || "Errore nella generazione dell'anteprima");
      setPdfBlob(null); setPdfUrl(null);
    } finally {
      setGenLoading(false);
    }
  };

  const downloadPdf = () => {
    if (!pdfBlob) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(pdfBlob);
    a.download = `${tpl?.name || "anteprima"}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const items = tab === "policy" ? policyItems : quoteItems;
  const query = tab === "policy" ? policyQuery : quoteQuery;
  const setQuery = tab === "policy" ? setPolicyQuery : setQuoteQuery;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="preview-with-data-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            Anteprima con dati reali — {tpl?.name || "Modello"}
          </DialogTitle>
          <DialogDescription>
            Seleziona una polizza o un preventivo per generare il PDF con i dati veri e verificare il rendering.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 overflow-hidden">
          {/* Colonna sinistra: sorgente dati */}
          <div className="border border-slate-200 rounded-md p-3 flex flex-col overflow-hidden bg-slate-50/40">
            <div className="flex gap-1 mb-2 shrink-0">
              <button
                type="button"
                onClick={() => setTab("policy")}
                className={`flex-1 text-xs py-1.5 rounded font-semibold transition-colors ${
                  tab === "policy" ? "bg-emerald-600 text-white" : "bg-white text-slate-700 border border-slate-300 hover:bg-emerald-50"
                }`}
                data-testid="preview-tab-policy"
              >
                📋 Polizza
              </button>
              <button
                type="button"
                onClick={() => setTab("quote")}
                className={`flex-1 text-xs py-1.5 rounded font-semibold transition-colors ${
                  tab === "quote" ? "bg-emerald-600 text-white" : "bg-white text-slate-700 border border-slate-300 hover:bg-emerald-50"
                }`}
                data-testid="preview-tab-quote"
              >
                💬 Preventivo
              </button>
            </div>
            <div className="relative mb-2 shrink-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder={tab === "policy" ? "Cerca per n°, targa, compagnia…" : "Cerca per n° preventivo, tipo…"}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-7 h-8 text-xs bg-white"
                data-testid="preview-search-input"
              />
            </div>
            <div
              className="flex-1 space-y-1 pr-1"
              style={{ overflowY: "auto", minHeight: 0, maxHeight: "60vh", overscrollBehavior: "contain" }}
              data-testid="preview-src-list"
              onWheel={(e) => {
                // Il Dialog Radix può bloccare gli eventi wheel: gestione manuale.
                e.stopPropagation();
                const el = e.currentTarget;
                const atTop = el.scrollTop === 0 && e.deltaY < 0;
                const atBottom = Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight && e.deltaY > 0;
                if (!atTop && !atBottom) {
                  el.scrollTop += e.deltaY;
                  e.preventDefault();
                }
              }}
              onTouchMove={(e) => e.stopPropagation()}
            >
              {searching && items.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Ricerca…</div>
              )}
              {!searching && items.length === 0 && (
                <div className="text-xs text-slate-500 text-center py-6">Nessun risultato</div>
              )}
              {items.length > 0 && (
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-1 pb-1 sticky top-0 bg-slate-50/95 backdrop-blur-sm" data-testid="preview-src-count">
                  {items.length} {tab === "policy" ? (items.length === 1 ? "polizza" : "polizze") : (items.length === 1 ? "preventivo" : "preventivi")} · Ordinati per cognome
                </div>
              )}
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => generatePreview(it.id)}
                  className={`w-full text-left px-2 py-1.5 rounded border text-xs transition-colors ${
                    selectedId === it.id ? "bg-emerald-100 border-emerald-500" : "bg-white border-slate-200 hover:bg-emerald-50 hover:border-emerald-300"
                  }`}
                  data-testid={`preview-src-${it.id}`}
                >
                  <div className="font-semibold text-slate-800 truncate">{it.label}</div>
                  {it.subtitle && <div className="text-[10px] text-slate-500 truncate mt-0.5">{it.subtitle}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Colonna destra: PDF preview */}
          <div className="border border-slate-200 rounded-md overflow-hidden bg-slate-100 flex flex-col">
            {genLoading && (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Generazione anteprima…
              </div>
            )}
            {!genLoading && genErr && (
              <div className="flex-1 flex items-center justify-center text-sm text-rose-600 p-4 text-center">{genErr}</div>
            )}
            {!genLoading && !genErr && pdfUrl && (
              <iframe
                src={pdfUrl}
                title="Anteprima PDF"
                className="w-full h-full min-h-[420px] bg-white"
                data-testid="preview-pdf-iframe"
              />
            )}
            {!genLoading && !genErr && !pdfUrl && (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400 p-4 text-center">
                Seleziona una polizza o un preventivo per vedere l&apos;anteprima con i dati reali.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="preview-close">Chiudi</Button>
          {pdfBlob && (
            <Button
              onClick={downloadPdf}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="preview-download"
            >
              <Download className="w-4 h-4 mr-1" /> Scarica PDF
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

